import type {
  AssignPlanInputDTO,
  OrganizationBillingInfoDTO,
  PlanCatalogAddonDTO,
  PlanCatalogDTO,
} from '@kanbu/schema/contracts';
import type { Currency } from '@kanbu/schema/enums';
import { formatUtils } from '@kanbu/shared';
import { Trans, useLingui } from '@lingui/react/macro';
import { Link, useLocation } from '@tanstack/react-router';
import { Button, SelectPrimitive as Select, toast } from '@utima/ui';
import {
  Banknote,
  CreditCard,
  Mail,
  SquareArrowOutUpRight,
} from 'lucide-react';
import { useMemo, useState } from 'react';

import { Card } from '@/components/card/Card';

import {
  BILLING_PERIOD_OPTIONS,
  catalogPriceMatchesPeriod,
  getBillingPeriodOption,
  validateMixedIntervalAlignment,
  type BillingPeriodKey,
} from './billingPeriods';
import { FieldLabelWithHint } from './FieldLabelWithHint';
import {
  type CollectionMethodTarget,
  selectClassName,
} from './planManagementShared';

export type AssignPlanFormValues = Omit<AssignPlanInputDTO, 'organizationId'>;

/**
 * Subset of the billing info needed to preview the invoice identity. Kept
 * structural (not the full DTO) so the serialized TRPC query result — which
 * carries string dates and may be null — assigns cleanly.
 */
type BillingInfoPreview = Pick<
  OrganizationBillingInfoDTO,
  | 'companyName'
  | 'registrationNumber'
  | 'vatId'
  | 'addressLine1'
  | 'addressLine2'
  | 'city'
  | 'postalCode'
  | 'country'
  | 'billingEmail'
  | 'invoiceCcEmail'
>;

type BasePlanOption = {
  priceId: string;
  label: string;
  tier: string;
  amount: number;
  isCustomEnterprise: boolean;
};

type AssignPlanCardProps = {
  organizationId: string;
  orgCurrency: Currency;
  catalog?: PlanCatalogDTO;
  billingInfo?: BillingInfoPreview | null;
  /**
   * Net payment terms + DUZP are SuperAdmin-only invoice settings, owned by the
   * parent and rendered in the SuperAdmin controls card. They are passed here
   * only to build the assign payload (partners use the defaults).
   */
  daysUntilDue: number;
  duzpDate: string;
  disabled?: boolean;
  isAssigning?: boolean;
  onAssign: (values: AssignPlanFormValues) => Promise<void>;
};

export function AssignPlanCard({
  organizationId,
  orgCurrency,
  catalog,
  billingInfo,
  daysUntilDue,
  duzpDate,
  disabled,
  isAssigning,
  onAssign,
}: AssignPlanCardProps) {
  const { t } = useLingui();
  const [selectedPriceId, setSelectedPriceId] = useState('');
  const [baseBillingPeriod, setBaseBillingPeriod] =
    useState<BillingPeriodKey>('month');
  const [addonPeriods, setAddonPeriods] = useState<
    Record<string, BillingPeriodKey>
  >({});
  const [selectedAddonKeys, setSelectedAddonKeys] = useState<string[]>([]);
  const [selectedSetupFeeIds, setSelectedSetupFeeIds] = useState<string[]>([]);
  const [collectionTarget, setCollectionTarget] =
    useState<CollectionMethodTarget>('bank_transfer');

  const registrationNumber = billingInfo?.registrationNumber?.trim();
  const vatId = billingInfo?.vatId?.trim();
  const billingEmail = billingInfo?.billingEmail?.trim();
  const hasTaxId = Boolean(registrationNumber || vatId);

  // Bank-transfer invoices legally require a tax id; every invoice needs an
  // address to send to. Block assign until the billing setup is sufficient.
  const billingReady =
    Boolean(billingEmail) && (collectionTarget !== 'bank_transfer' || hasTaxId);

  const billingPeriodLabels = useMemo(
    (): Record<BillingPeriodKey, string> => ({
      month: t`Monthly`,
      quarter: t`Quarterly`,
      half_year: t`Half-yearly`,
      year: t`Yearly`,
    }),
    [t],
  );

  const billingPeriodShortLabels = useMemo(
    (): Record<BillingPeriodKey, string> => ({
      month: t`mo`,
      quarter: t`qtr`,
      half_year: t`6 mo`,
      year: t`yr`,
    }),
    [t],
  );

  const selectedPeriodOption = getBillingPeriodOption(baseBillingPeriod);

  const basePlanOptions = useMemo((): BasePlanOption[] => {
    if (!catalog) {
      return [];
    }

    // A product can carry more than one price at the same interval — e.g. a
    // grandfathered/legacy price kept active alongside the current one.
    // Surface every match rather than picking the first: the amount in the
    // label already disambiguates them, and silently hiding one leaves it
    // unreachable in the UI even though it's a live, assignable Stripe price.
    return catalog.basePlans.flatMap(plan => {
      const matchingPrices = plan.prices.filter(candidate =>
        catalogPriceMatchesPeriod(candidate, selectedPeriodOption),
      );

      return matchingPrices.map(price => ({
        priceId: price.priceId,
        label: plan.isCustomEnterprise
          ? `${plan.productName} (Enterprise)`
          : plan.productName,
        tier: plan.tier,
        amount: price.amount,
        isCustomEnterprise: plan.isCustomEnterprise,
      }));
    });
  }, [catalog, selectedPeriodOption]);

  const periodHasBasePrices = basePlanOptions.length > 0;

  const recurringAddons = useMemo(
    () => catalog?.addons.filter(addon => addon.prices.length > 0) ?? [],
    [catalog],
  );

  const setupFeeAddons = useMemo(
    () => catalog?.addons.filter(addon => addon.oneTimePrices.length > 0) ?? [],
    [catalog],
  );

  const selectedPlan = basePlanOptions.find(
    option => option.priceId === selectedPriceId,
  );

  const findAddonPrice = (
    addon: PlanCatalogAddonDTO,
    periodKey: BillingPeriodKey,
  ) => {
    const period = getBillingPeriodOption(periodKey);

    return addon.prices.find(price => catalogPriceMatchesPeriod(price, period));
  };

  const selectedRecurringAddons = recurringAddons.filter(addon =>
    selectedAddonKeys.includes(addon.addonKey),
  );

  const selectedPeriodKeys = useMemo((): BillingPeriodKey[] => {
    const keys: BillingPeriodKey[] = [baseBillingPeriod];

    for (const addon of selectedRecurringAddons) {
      keys.push(addonPeriods[addon.addonKey] ?? baseBillingPeriod);
    }

    return keys;
  }, [addonPeriods, baseBillingPeriod, selectedRecurringAddons]);

  const intervalAlignment = useMemo(
    () => validateMixedIntervalAlignment(selectedPeriodKeys),
    [selectedPeriodKeys],
  );

  const recurringAddonTotal = selectedRecurringAddons.reduce((sum, addon) => {
    const price = findAddonPrice(
      addon,
      addonPeriods[addon.addonKey] ?? baseBillingPeriod,
    );

    return sum + (price?.amount ?? 0);
  }, 0);

  const selectedSetupFees = setupFeeAddons.flatMap(addon =>
    addon.oneTimePrices.filter(price =>
      selectedSetupFeeIds.includes(price.priceId),
    ),
  );

  const oneTimeTotal = selectedSetupFees.reduce(
    (sum, price) => sum + price.amount,
    0,
  );

  const estimatedTotal =
    (selectedPlan?.amount ?? 0) + recurringAddonTotal + oneTimeTotal;

  const hasMixedPeriods = selectedRecurringAddons.some(
    addon =>
      (addonPeriods[addon.addonKey] ?? baseBillingPeriod) !== baseBillingPeriod,
  );

  const setAddonPeriod = (addonKey: string, period: BillingPeriodKey) => {
    setAddonPeriods(current => ({ ...current, [addonKey]: period }));
  };

  const toggleAddon = (addonKey: string) => {
    setSelectedAddonKeys(current => {
      if (current.includes(addonKey)) {
        return current.filter(key => key !== addonKey);
      }

      setAddonPeriods(periods => ({
        ...periods,
        [addonKey]: baseBillingPeriod,
      }));

      return [...current, addonKey];
    });
  };

  const toggleSetupFee = (priceId: string) => {
    setSelectedSetupFeeIds(current =>
      current.includes(priceId)
        ? current.filter(id => id !== priceId)
        : [...current, priceId],
    );
  };

  const handleAssign = async () => {
    if (!selectedPlan) {
      toast.warning(t`Please select a plan first.`);

      return;
    }

    if (!intervalAlignment.valid) {
      toast.error(
        t`Selected billing periods are not compatible for one subscription.`,
      );

      return;
    }

    if (collectionTarget === 'bank_transfer' && !registrationNumber && !vatId) {
      toast.error(
        t`Organization billing information is missing IČO or VAT ID.`,
      );

      return;
    }

    const items: AssignPlanFormValues['items'] = [
      { stripePriceId: selectedPlan.priceId, productType: 'plan', quantity: 1 },
    ];

    for (const addon of selectedRecurringAddons) {
      const period = addonPeriods[addon.addonKey] ?? baseBillingPeriod;
      const price = findAddonPrice(addon, period);

      if (!price) {
        toast.error(
          t`Add-on "${addon.productName}" has no ${billingPeriodLabels[period]} price configured.`,
        );

        return;
      }

      items.push({
        stripePriceId: price.priceId,
        productType: 'addon',
        addonKey: addon.addonKey,
        quantity: 1,
      });
    }

    await onAssign({
      items,
      oneTimeFeePriceIds: selectedSetupFeeIds.length
        ? selectedSetupFeeIds
        : undefined,
      collectionMethodTarget: collectionTarget,
      daysUntilDue,
      duzpDate: duzpDate || undefined,
      sendNow: true,
      replaceExisting: false,
    });
  };

  return (
    <Card title={t`Assign plan`} className='mb-6'>
      <div className='grid grid-cols-1 gap-6 lg:grid-cols-2'>
        <div className='space-y-4'>
          <div className='space-y-2'>
            <FieldLabelWithHint
              htmlFor='billing-period-select'
              label={<Trans>Base plan billing period</Trans>}
              hint={
                <Trans>
                  Billing period for the main subscription tier. Add-ons can use
                  a different period below.
                </Trans>
              }
            />
            <Select.Root
              value={baseBillingPeriod}
              onValueChange={value => {
                setBaseBillingPeriod(value as BillingPeriodKey);
                setSelectedPriceId('');
              }}
              disabled={disabled}
            >
              <Select.Trigger id='billing-period-select' className='w-full'>
                <Select.Value placeholder={t`Select billing period`} />
              </Select.Trigger>
              <Select.Content>
                {BILLING_PERIOD_OPTIONS.map(option => (
                  <Select.Item key={option.key} value={option.key}>
                    {billingPeriodLabels[option.key]}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Root>
          </div>

          <div className='space-y-2'>
            <label htmlFor='assign-plan-select' className='text-sm font-medium'>
              <Trans>Base plan</Trans>
            </label>
            {!periodHasBasePrices && (
              <p className='text-muted-foreground text-sm'>
                <Trans>
                  No base-plan prices exist in Stripe for this currency and
                  billing period. Create the matching price (interval_count 3
                  for quarterly, 6 for half-yearly) to enable selection.
                </Trans>
              </p>
            )}
            <select
              id='assign-plan-select'
              value={selectedPriceId}
              onChange={event => setSelectedPriceId(event.target.value)}
              className={selectClassName}
              disabled={disabled || !periodHasBasePrices}
            >
              <option value=''>{t`-- Select plan --`}</option>
              {basePlanOptions.map(option => (
                <option key={option.priceId} value={option.priceId}>
                  {`${option.label} — ${formatUtils.currency(option.amount, orgCurrency)} / ${billingPeriodShortLabels[baseBillingPeriod]}`}
                </option>
              ))}
            </select>
          </div>

          {recurringAddons.length > 0 && (
            <div className='space-y-2'>
              <FieldLabelWithHint
                label={<Trans>Add-ons</Trans>}
                hint={
                  <Trans>
                    Each add-on can bill on its own period. Stripe invoices
                    items together when periods align, separately when they do
                    not.
                  </Trans>
                }
              />
              <div className='space-y-2 rounded-md border p-3'>
                {recurringAddons.map(addon => {
                  const isSelected = selectedAddonKeys.includes(addon.addonKey);
                  const addonPeriod =
                    addonPeriods[addon.addonKey] ?? baseBillingPeriod;
                  const price = findAddonPrice(addon, addonPeriod);

                  return (
                    <div
                      key={addon.addonKey}
                      className='space-y-2 rounded-md border border-transparent p-2 has-[:checked]:border-muted has-[:checked]:bg-muted/20'
                    >
                      <label className='flex cursor-pointer items-start gap-2 text-sm'>
                        <input
                          type='checkbox'
                          checked={isSelected}
                          onChange={() => toggleAddon(addon.addonKey)}
                          disabled={disabled}
                          className='mt-0.5'
                        />
                        <span className='flex-1'>
                          <span className='font-medium'>
                            {addon.productName}
                          </span>
                          {price ? (
                            <span className='text-muted-foreground'>
                              {' '}
                              —{' '}
                              {formatUtils.currency(
                                price.amount,
                                orgCurrency,
                              )}{' '}
                              / {billingPeriodShortLabels[addonPeriod]}
                            </span>
                          ) : (
                            <span className='text-amber-600'>
                              {' '}
                              — {t`no price for selected period`}
                            </span>
                          )}
                        </span>
                      </label>

                      {isSelected && (
                        <div className='ml-6 space-y-1'>
                          <label
                            htmlFor={`addon-period-${addon.addonKey}`}
                            className='text-muted-foreground text-xs font-medium'
                          >
                            <Trans>Add-on billing period</Trans>
                          </label>
                          <Select.Root
                            value={addonPeriod}
                            onValueChange={value =>
                              setAddonPeriod(
                                addon.addonKey,
                                value as BillingPeriodKey,
                              )
                            }
                            disabled={disabled}
                          >
                            <Select.Trigger
                              id={`addon-period-${addon.addonKey}`}
                              className='w-full'
                            >
                              <Select.Value />
                            </Select.Trigger>
                            <Select.Content>
                              {BILLING_PERIOD_OPTIONS.map(option => (
                                <Select.Item
                                  key={option.key}
                                  value={option.key}
                                >
                                  {billingPeriodLabels[option.key]}
                                </Select.Item>
                              ))}
                            </Select.Content>
                          </Select.Root>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {!intervalAlignment.valid && (
                <p className='text-sm text-red-600'>
                  <Trans>
                    These billing periods cannot be combined on one
                    subscription. Each period must be a multiple of the shortest
                    selected period.
                  </Trans>
                </p>
              )}
            </div>
          )}

          {setupFeeAddons.length > 0 && (
            <div className='space-y-2'>
              <FieldLabelWithHint
                label={<Trans>One-time setup fees</Trans>}
                hint={
                  <Trans>
                    Billed once on the first invoice only; not on renewals.
                  </Trans>
                }
              />
              <div className='space-y-1 rounded-md border p-3'>
                {setupFeeAddons.flatMap(addon =>
                  addon.oneTimePrices.map(price => (
                    <label
                      key={price.priceId}
                      className='flex cursor-pointer items-center gap-2 text-sm'
                    >
                      <input
                        type='checkbox'
                        checked={selectedSetupFeeIds.includes(price.priceId)}
                        onChange={() => toggleSetupFee(price.priceId)}
                        disabled={disabled}
                      />
                      <span>
                        {addon.productName} —{' '}
                        {formatUtils.currency(price.amount, orgCurrency)} (
                        {t`one-time`})
                      </span>
                    </label>
                  )),
                )}
              </div>
            </div>
          )}
        </div>

        <div className='space-y-4'>
          <div className='space-y-2'>
            <FieldLabelWithHint
              label={<Trans>Payment method</Trans>}
              hint={
                <Trans>
                  Choose how the customer is expected to pay the first invoice.
                  Renewals follow the method they actually use.
                </Trans>
              }
            />
            <div className='flex gap-2'>
              <label className='flex flex-1 cursor-pointer items-center gap-2 rounded-md border p-3 text-sm has-[:checked]:border-primary has-[:checked]:bg-muted/30'>
                <input
                  type='radio'
                  name='collection-target'
                  checked={collectionTarget === 'bank_transfer'}
                  onChange={() => setCollectionTarget('bank_transfer')}
                />
                <Banknote className='size-4 shrink-0' />
                <span className='font-medium'>
                  <Trans>Bank transfer</Trans>
                </span>
              </label>
              <label className='flex flex-1 cursor-pointer items-center gap-2 rounded-md border p-3 text-sm has-[:checked]:border-primary has-[:checked]:bg-muted/30'>
                <input
                  type='radio'
                  name='collection-target'
                  checked={collectionTarget === 'card'}
                  onChange={() => setCollectionTarget('card')}
                />
                <CreditCard className='size-4 shrink-0' />
                <span className='font-medium'>
                  <Trans>Card</Trans>
                </span>
              </label>
            </div>
          </div>

          <BillingDetailsPreview
            organizationId={organizationId}
            billingInfo={billingInfo}
            collectionTarget={collectionTarget}
          />

          {selectedPlan && (
            <div className='space-y-2 rounded-md bg-muted/40 p-3 text-sm'>
              <p className='font-medium'>
                <Trans>Estimated first invoice</Trans>
              </p>
              <p className='text-lg font-semibold'>
                {formatUtils.currency(estimatedTotal, orgCurrency)}
              </p>
              <ul className='text-muted-foreground space-y-1'>
                <li>
                  {selectedPlan.label} —{' '}
                  {formatUtils.currency(selectedPlan.amount, orgCurrency)} /{' '}
                  {billingPeriodShortLabels[baseBillingPeriod]}
                </li>
                {selectedRecurringAddons.map(addon => {
                  const period =
                    addonPeriods[addon.addonKey] ?? baseBillingPeriod;
                  const price = findAddonPrice(addon, period);

                  return (
                    <li key={addon.addonKey}>
                      {addon.productName} —{' '}
                      {price
                        ? `${formatUtils.currency(price.amount, orgCurrency)} / ${billingPeriodShortLabels[period]}`
                        : t`no price`}
                    </li>
                  );
                })}
                {oneTimeTotal > 0 && (
                  <li>
                    <Trans>Setup fees (one-time)</Trans> —{' '}
                    {formatUtils.currency(oneTimeTotal, orgCurrency)}
                  </li>
                )}
              </ul>
              {hasMixedPeriods && intervalAlignment.valid && (
                <p className='text-muted-foreground'>
                  <Trans>
                    Mixed periods: later renewals may split into separate
                    invoices when item periods do not align.
                  </Trans>
                </p>
              )}
            </div>
          )}

          <Button
            onClick={handleAssign}
            loading={isAssigning}
            disabled={
              disabled ||
              !selectedPlan ||
              !intervalAlignment.valid ||
              !billingReady
            }
            className='w-full sm:w-auto'
          >
            <Trans>Assign and activate</Trans>
          </Button>
        </div>
      </div>
    </Card>
  );
}

type BillingDetailsPreviewProps = {
  organizationId: string;
  billingInfo?: BillingInfoPreview | null;
  collectionTarget: CollectionMethodTarget;
};

/**
 * Full billing-identity (fakturační údaje) preview shown before assigning a
 * plan. These are the company details stamped on the Stripe invoice, so the
 * person assigning can confirm the legal identity is complete — especially for
 * bank-transfer assigns, which require IČO or VAT ID.
 */
function BillingDetailsPreview({
  organizationId,
  billingInfo,
  collectionTarget,
}: BillingDetailsPreviewProps) {
  const { t } = useLingui();
  const location = useLocation();

  const companyName = billingInfo?.companyName?.trim();
  const registrationNumber = billingInfo?.registrationNumber?.trim();
  const vatId = billingInfo?.vatId?.trim();
  const addressLine1 = billingInfo?.addressLine1?.trim();
  const addressLine2 = billingInfo?.addressLine2?.trim();
  const city = billingInfo?.city?.trim();
  const postalCode = billingInfo?.postalCode?.trim();
  const country = billingInfo?.country?.trim();
  const billingEmail = billingInfo?.billingEmail?.trim();
  const invoiceCcEmail = billingInfo?.invoiceCcEmail?.trim();

  const cityLine = [postalCode, city].filter(Boolean).join(' ');
  const hasTaxId = Boolean(registrationNumber || vatId);
  const hasAddress = Boolean(addressLine1 || cityLine);
  const isComplete = Boolean(companyName) && hasTaxId && hasAddress;

  const rows: Array<{ id: string; label: string; value?: string }> = [
    { id: 'company', label: t`Company`, value: companyName },
    { id: 'ico', label: t`Reg. no. (IČO)`, value: registrationNumber },
    { id: 'dic', label: t`VAT ID (DIČ)`, value: vatId },
    { id: 'addr1', label: t`Address`, value: addressLine1 },
    { id: 'addr2', label: '', value: addressLine2 },
    { id: 'city', label: '', value: cityLine || undefined },
    { id: 'country', label: t`Country`, value: country },
    { id: 'email', label: t`Billing e-mail`, value: billingEmail },
    { id: 'cc', label: t`Invoice CC`, value: invoiceCcEmail },
  ];

  return (
    <div className='space-y-2 rounded-md border border-dashed p-3 text-sm'>
      <div className='flex items-center justify-between'>
        <span className='font-medium'>
          <Trans>Billing details</Trans>
        </span>
        {!isComplete && (
          <span className='inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'>
            <Trans>Incomplete</Trans>
          </span>
        )}
      </div>

      {billingInfo ? (
        <dl className='space-y-0.5'>
          {rows
            .filter(row => row.value)
            .map(row => (
              <div key={row.id} className='flex gap-2'>
                <dt className='text-muted-foreground w-32 shrink-0'>
                  {row.label}
                </dt>
                <dd>{row.value}</dd>
              </div>
            ))}
        </dl>
      ) : (
        <p className='text-amber-600'>
          <Trans>No billing details set for this organization yet.</Trans>
        </p>
      )}

      {collectionTarget === 'bank_transfer' && !hasTaxId && (
        <p className='text-amber-600'>
          <Trans>
            Bank-transfer assign requires IČO or VAT ID. Fill the billing
            details first.
          </Trans>
        </p>
      )}

      <div className='text-muted-foreground flex items-start gap-2 border-t pt-2'>
        <Mail className='mt-0.5 size-4 shrink-0' />
        <p>
          {billingEmail ? (
            <Trans>
              Invoices are sent to{' '}
              <span className='font-medium text-foreground'>
                {billingEmail}
              </span>
              .
            </Trans>
          ) : (
            <span className='text-amber-600'>
              <Trans>No billing e-mail set — required to assign a plan.</Trans>
            </span>
          )}
        </p>
      </div>

      <Link
        to='/organizations/$organizationId/billing'
        params={{ organizationId }}
        search={{ redirect: location.href }}
        className='inline-flex items-center gap-1 font-medium text-primary hover:underline'
      >
        <Trans>Manage company billing details</Trans>
        <SquareArrowOutUpRight className='size-3.5' />
      </Link>
    </div>
  );
}
